'use client';

/**
 * Per-stage live progress strip for the create-proposal dialog. Subscribes
 * to the `CreateMarketProgressState` exposed by `useCreateProposalMutation`
 * and renders a vertical checklist as each SSE event lands.
 *
 *   [✓] Preparing metadata
 *   [✓] Pinning to IPFS
 *   [✓] Creating market on chain      shell signature: 4xK…
 *   [●] Initializing outcomes (2/3)
 *   [ ] Finalizing
 *
 * On terminal `error` the strip stays mounted but the dialog wrapper
 * swaps in a failure-view footer with a Retry button (autoclean already
 * ran server-side, so retry is a fresh submission).
 */

import { Icon } from '@/components/ui/icon';
import { Cancel01Icon, Loading03Icon, Tick02Icon } from '@/components/ui/icon/glyphs';
import type { CreateMarketProgressState } from '@/hooks/proposals/use-create-proposal';
import { cn } from '@/lib/ui/utils';

type StageStatus = 'idle' | 'active' | 'done' | 'failed';

interface StageRowProps {
  status: StageStatus;
  label: string;
  detail?: string;
}

function StageRow({ status, label, detail }: StageRowProps): React.ReactNode {
  return (
    <div className="flex items-center gap-2 text-body">
      <span
        className={cn(
          'inline-flex size-5 shrink-0 items-center justify-center rounded-full',
          status === 'done' && 'bg-feedback-success-accent/15 text-feedback-success-content',
          status === 'active' && 'bg-background-brand text-foreground-brand',
          status === 'idle' && 'bg-background-tertiary text-foreground-tertiary',
          status === 'failed' && 'bg-feedback-error-accent/15 text-feedback-error-content',
        )}
      >
        {status === 'done' && <Icon icon={Tick02Icon} className="size-3" />}
        {status === 'active' && <Icon icon={Loading03Icon} className="size-3 animate-spin" />}
        {status === 'failed' && <Icon icon={Cancel01Icon} className="size-3" />}
      </span>
      <span
        className={cn(
          'flex-1 min-w-0 truncate',
          status === 'idle' && 'text-foreground-tertiary',
          status === 'failed' && 'text-feedback-error-content',
        )}
      >
        {label}
      </span>
      {detail !== undefined && detail.length > 0 && (
        <span className="text-caption font-mono text-foreground-tertiary">{detail}</span>
      )}
    </div>
  );
}

function shortenSignature(sig: string | null): string {
  if (sig === null || sig.length < 12) return sig ?? '';
  return `${sig.slice(0, 6)}…${sig.slice(-4)}`;
}

export interface CreateMarketProgressStripProps {
  progress: CreateMarketProgressState;
}

export function CreateMarketProgressStrip({
  progress,
}: CreateMarketProgressStripProps): React.ReactNode {
  const isFailed = progress.status === 'failed';
  // `error.stage` is the orchestrator-emitted stage marker. It's the
  // authoritative answer to 'which row failed'; the per-row guards below
  // would otherwise fall through to 'idle' on failures that happen
  // BEFORE the stage's signature gets recorded (the orchestrator sets
  // shellSubmitted only AFTER submitTransaction succeeds, so a shell-
  // submit failure leaves shellSubmitted=null and would visually skip
  // the failure marker without this override).
  const failedStage = isFailed ? (progress.error?.stage ?? null) : null;

  const prepStatus: StageStatus =
    failedStage === 'preparing'
      ? 'failed'
      : progress.status === 'idle'
        ? 'idle'
        : progress.status === 'preparing'
          ? 'active'
          : 'done';

  const shellStatus: StageStatus =
    failedStage === 'shell'
      ? 'failed'
      : progress.shellSubmitted === null
        ? progress.status === 'shell'
          ? 'active'
          : 'idle'
        : progress.shellConfirmed
          ? 'done'
          : 'active';

  const outcomesStatus: StageStatus =
    failedStage === 'outcomes'
      ? 'failed'
      : !progress.shellConfirmed
        ? 'idle'
        : progress.outcomesConfirmed >= progress.outcomesTotal && progress.outcomesTotal > 0
          ? 'done'
          : 'active';

  const finalizeStatus: StageStatus =
    failedStage === 'finalize'
      ? 'failed'
      : progress.outcomesConfirmed < progress.outcomesTotal || progress.outcomesTotal === 0
        ? 'idle'
        : progress.finalizeConfirmed
          ? 'done'
          : 'active';

  return (
    <div className="flex flex-col gap-3 rounded-panel-row border border-border-primary bg-background-tertiary p-4">
      <StageRow status={prepStatus} label="Preparing market" />
      <StageRow
        status={shellStatus}
        label="Creating market on chain"
        detail={shortenSignature(progress.shellSubmitted)}
      />
      <StageRow
        status={outcomesStatus}
        label={
          progress.outcomesTotal === 0
            ? 'Initializing outcomes'
            : `Initializing outcomes (${String(progress.outcomesConfirmed)}/${String(progress.outcomesTotal)})`
        }
      />
      <StageRow
        status={finalizeStatus}
        label="Finalizing"
        detail={shortenSignature(progress.finalizeSubmitted)}
      />
      {isFailed && progress.error !== null && (
        <p className="rounded-overlay-item border bg-background-primary p-3 text-body text-feedback-error-content">
          {progress.error.message}
        </p>
      )}
    </div>
  );
}
